import {
  CopyIcon,
  PaperPlaneIcon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import {
  MessageTemplateSummary,
  MessageTemplatesService,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import Pager from "components/Dashboard/PeoplePanel/Pager";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { formatDate } from "components/Forms/ResponseDialog";
import { usePaged } from "components/Forms/usePaged";
import { openEmailBrandSettings } from "components/ProfileDropdown/EmailBrandSettings";
import { OrgAdminShell } from "pages/org-performance/OrgAdminPage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Notifications from "util/notifications";

/** Messages waiting for approval are answered in Automations › Activity. */
const WAITING_FOR_APPROVAL = "/automations?tab=activity";

/** "Email and text", "Email" or "Text". */
function versionWords(template: MessageTemplateSummary) {
  if (template.has_email && template.has_sms) return "Email and text";
  if (template.has_email) return "Email";
  if (template.has_sms) return "Text";
  return "Nothing written yet";
}

function TemplatesList({ orgId }: { orgId: number }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The approval queue moved to Automations › Activity: old links go there.
  const oldQueueLink = params.get("tab") === "queue";
  useEffect(() => {
    if (oldQueueLink) navigate(WAITING_FOR_APPROVAL, { replace: true });
  }, [oldQueueLink, navigate]);
  const [templates, setTemplates] = useState<MessageTemplateSummary[]>();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<MessageTemplateSummary | null>(null);
  const [duplicating, setDuplicating] = useState<MessageTemplateSummary | null>(
    null
  );
  const [copying, setCopying] = useState(false);
  const [sending, setSending] = useState<MessageTemplateSummary | null>(null);
  const templatesPage = usePaged(templates);

  // Only the latest load's answer counts, whatever order the answers come in.
  const loads = useRef(0);
  const load = useCallback(() => {
    const ask = ++loads.current;
    MessageTemplatesService.getMessageTemplates({ orgId })
      .then((found) => ask === loads.current && setTemplates(found))
      .catch(
        (e) =>
          ask === loads.current &&
          Notifications.error(
            errorText(e, "Message templates could not be loaded.")
          )
      );
  }, [orgId]);

  useEffect(load, [load]);

  function createTemplate() {
    setCreating(true);
    MessageTemplatesService.createMessageTemplate({ orgId })
      .then((template) => navigate(`/message-templates/${template.id}`))
      .catch((e) => {
        Notifications.error(errorText(e, "The template could not be created."));
        setCreating(false);
      });
  }

  /** A copy to start a new template from, opened in the editor. */
  function duplicateTemplate(template: MessageTemplateSummary) {
    setCopying(true);
    MessageTemplatesService.duplicateMessageTemplate({
      templateId: template.id,
    })
      .then((copy) => navigate(`/message-templates/${copy.id}`))
      .catch((e) => {
        Notifications.error(errorText(e, "The template could not be copied."));
        setCopying(false);
      });
  }

  function deleteTemplate(template: MessageTemplateSummary) {
    MessageTemplatesService.deleteMessageTemplate({ templateId: template.id })
      .then(() => {
        setDeleting(null);
        setTemplates((prev) =>
          (prev || []).filter((t) => t.id !== template.id)
        );
        Notifications.success(`${template.name} was deleted.`);
      })
      .catch((e) =>
        Notifications.error(errorText(e, "The template could not be deleted."))
      );
  }

  const newTemplateButton = (
    <Button
      color={ButtonColor.GREEN}
      size={ButtonSize.SMALL}
      onClick={createTemplate}
      disabled={creating}
    >
      <PlusIcon /> New template
    </Button>
  );

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">Message Templates</h2>
        <span className="inline-flex gap-2">
          <Button
            color={ButtonColor.PURPLE}
            fill={ButtonFill.HOLLOW}
            size={ButtonSize.SMALL}
            onClick={openEmailBrandSettings}
          >
            Email brand
          </Button>
          {templates && templates.length > 0 && newTemplateButton}
        </span>
      </div>

      {!templates && (
        <div className="mt-6 animate-pulse space-y-3">
          <div className="h-10 rounded bg-gray-200" />
          <div className="h-10 rounded bg-gray-100" />
        </div>
      )}

      {templates && templates.length === 0 && (
        <div className="mt-10 mb-6 flex flex-col items-center text-center">
          <p className="text-lg font-semibold text-gray-900">
            No message templates yet
          </p>
          <p className="mt-2 max-w-md text-sm text-gray-600">
            Write a message once, with an email and a text version, and send it
            from any message box. Each person gets it with their own details,
            and automations can send it for you.
          </p>
          <div className="mt-5">{newTemplateButton}</div>
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          {templatesPage.paged && (
            <div className="mb-3">
              <Pager
                page={templatesPage.page}
                pageCount={templatesPage.pageCount}
                onPage={templatesPage.setPage}
              />
            </div>
          )}
          <table className="w-full border-collapse bg-white rounded-lg">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3 text-sm font-bold">Name</th>
                <th className="text-left p-3 text-sm font-bold">Channel</th>
                <th className="text-left p-3 text-sm font-bold">
                  Awaiting approval
                </th>
                <th className="text-left p-3 text-sm font-bold">Last edited</th>
                <th className="p-3 text-sm font-bold text-right" />
              </tr>
            </thead>
            <tbody>
              {templatesPage.pageRows.map((template) => (
                <tr
                  key={template.id}
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/message-templates/${template.id}`)}
                >
                  <td className="p-3 text-sm font-medium text-gray-900">
                    {template.name}
                    {template.email_subject && template.has_email && (
                      <span className="block text-xs font-normal text-gray-500">
                        {template.email_subject}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {versionWords(template)}
                    {template.promotional && (
                      <span className="block text-xs text-gray-500">
                        Promotional
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {template.waiting_count > 0 ? (
                      <button
                        type="button"
                        className="font-medium text-orange-600 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(WAITING_FOR_APPROVAL);
                        }}
                      >
                        {template.waiting_count}
                      </button>
                    ) : (
                      0
                    )}
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {formatDate(template.modified_at)}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <span className="inline-flex gap-2">
                      <IconTip label="Edit">
                        <Button
                          color={ButtonColor.PURPLE}
                          size={ButtonSize.SMALL}
                          aria-label={`Edit ${template.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/message-templates/${template.id}`);
                          }}
                        >
                          <Pencil1Icon />
                        </Button>
                      </IconTip>
                      <IconTip label="Send">
                        <Button
                          color={ButtonColor.GREEN}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.SMALL}
                          aria-label={`Send ${template.name}`}
                          disabled={!template.has_email && !template.has_sms}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSending(template);
                          }}
                        >
                          <PaperPlaneIcon />
                        </Button>
                      </IconTip>
                      <IconTip label="Duplicate">
                        <Button
                          color={ButtonColor.PURPLE}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.SMALL}
                          aria-label={`Duplicate ${template.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDuplicating(template);
                          }}
                        >
                          <CopyIcon />
                        </Button>
                      </IconTip>
                      <IconTip label="Delete">
                        <Button
                          color={ButtonColor.RED}
                          fill={ButtonFill.HOLLOW}
                          size={ButtonSize.SMALL}
                          aria-label={`Delete ${template.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleting(template);
                          }}
                        >
                          <TrashIcon />
                        </Button>
                      </IconTip>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={sending !== null}
        onOpenChange={(open) => !open && setSending(null)}
      >
        {sending && (
          <ComposeMessageDialog
            org_id={orgId}
            initialTemplateId={sending.id}
            lookup="everyone"
          />
        )}
      </Dialog>

      <Dialog
        open={duplicating !== null}
        onOpenChange={(open) => !open && setDuplicating(null)}
      >
        {duplicating && (
          <DialogContent className="dialog-content">
            <h2 className="text-xl font-semibold mb-2">
              Duplicate {duplicating.name}?
            </h2>
            <p className="text-sm text-gray-700">
              This makes a new template called &quot;Copy of {duplicating.name}
              &quot; with the same email and text. The original doesn&apos;t
              change. The copy opens in the editor so you can change it.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.PURPLE}
                disabled={copying}
                onClick={() => duplicateTemplate(duplicating)}
              >
                Duplicate
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        {deleting && (
          <DialogContent className="dialog-content">
            <h2 className="text-xl font-semibold mb-2">
              Delete {deleting.name}?
            </h2>
            <p className="text-sm text-gray-700">
              It can no longer be sent, automations stop sending it, and its
              messages waiting for approval are cleared. What it already sent
              stays in Communication.
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                onClick={() => deleteTemplate(deleting)}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

export default function MessageTemplatesPage() {
  return (
    <OrgAdminShell>{(orgId) => <TemplatesList orgId={orgId} />}</OrgAdminShell>
  );
}
